import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Link from "@mui/material/Link";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { type FormEvent, useState } from "react";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import { getAuthErrorMessage } from "@bw/shared/auth-errors";
import { confirmReset } from "@bw/shared/firebase";

import { AuthShell } from "../components/AuthShell";
import { useDocumentTitle } from "../useDocumentTitle";

export function ResetPasswordPage() {
  useDocumentTitle("Reset password · Badminton Warriors");
  const [searchParams] = useSearchParams();
  const oobCode = searchParams.get("oobCode")?.trim() ?? "";
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);

    if (newPassword !== confirmPassword) {
      setError("The two passwords don't match.");
      return;
    }

    setSubmitting(true);
    try {
      await confirmReset(oobCode, newPassword);
      setSuccess(true);
    } catch (err) {
      setError(
        getAuthErrorMessage(
          err,
          "Reset failed. Request a new link and try again.",
        ),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthShell title="Reset password">
      <Paper variant="outlined" sx={{ p: { xs: 2.5, sm: 3 } }}>
        {!oobCode ? (
          <Stack spacing={2.5}>
            <Alert severity="error" role="alert">
              This reset link is invalid or has expired. Request a new one.
            </Alert>
            <Link
              component={RouterLink}
              to="/forgot-password"
              sx={{ alignSelf: "center" }}
            >
              Request a new link
            </Link>
          </Stack>
        ) : success ? (
          <Stack spacing={2.5}>
            <Alert severity="success" role="status">
              Your password has been reset. Sign in with your new password.
            </Alert>
            <Link
              component={RouterLink}
              to="/sign-in"
              sx={{ alignSelf: "center" }}
            >
              Back to sign in
            </Link>
          </Stack>
        ) : (
          <Stack component="form" onSubmit={onSubmit} spacing={2.25}>
            <Typography color="text.secondary" variant="body2">
              Enter a new password to finish resetting your account.
            </Typography>
            {error ? (
              <Alert severity="error" role="alert">
                {error}
              </Alert>
            ) : null}
            <TextField
              autoComplete="new-password"
              label="New password"
              onChange={(event) => setNewPassword(event.target.value)}
              required
              type="password"
              value={newPassword}
            />
            <TextField
              autoComplete="new-password"
              label="Confirm new password"
              onChange={(event) => setConfirmPassword(event.target.value)}
              required
              type="password"
              value={confirmPassword}
            />
            <Button
              disabled={submitting}
              fullWidth
              startIcon={
                submitting ? (
                  <CircularProgress
                    aria-hidden="true"
                    color="inherit"
                    size={16}
                  />
                ) : undefined
              }
              type="submit"
              variant="contained"
            >
              {submitting ? "Submitting…" : "Reset password"}
            </Button>
          </Stack>
        )}
      </Paper>
    </AuthShell>
  );
}
